import { ClockCircleOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Collapse, DatePicker, Form, Layout, message, Modal, Popconfirm, Select, Space, Tag, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/DataContext';
import { db } from '../../firebase';
import { uploadFullSeatLayout } from './SeatEvent';
import apis from '../../apis';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const PerformerSessionsSettings = () => {
    const { currentEventId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [venues, setVenues] = useState([]);
    const [sessionsSeatsGenerated, setSessionsSeatsGenerated] = useState({});
    const [planningEvent, setPlanningEvent] = useState(false);
    const [planningState, setPlanningState] = useState(null);
    const [planningError, setPlanningError] = useState(null);
    const [editingSlot, setEditingSlot] = useState(null);
    const [slotModalOpen, setSlotModalOpen] = useState(false);
    const [slotSaving, setSlotSaving] = useState(false);
    const [slotForm] = Form.useForm();

    // Modals state
    const [isDateModalVisible, setIsDateModalVisible] = useState(false);
    const [isTimeModalVisible, setIsTimeModalVisible] = useState(false);

    // Selection state
    const [selectedVenueId, setSelectedVenueId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);

    const [dateForm] = Form.useForm();
    const [timeForm] = Form.useForm();

    const fetchEventData = useCallback(async () => {
        setLoading(true);
        setPlanningError(null);
        setPlanningState(null);
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setVenues(data.venues || []);
                setSessionsSeatsGenerated(data.sessionsSeatsGenerated || {});
                const isPlanning = Boolean(data.competitionScheduleState);
                setPlanningEvent(isPlanning);
                if (isPlanning) {
                    const response = await apis.competitionPlanning.getState(currentEventId);
                    if (response?.error) throw new Error(response.error.data?.message || 'Could not load draft sessions.');
                    setPlanningState(response.data);
                } else {
                    setPlanningState(null);
                }
            }
        } catch (error) {
            console.error("Error fetching event data:", error);
            message.error("Failed to fetch performer sessions data");
            setPlanningError(error.message);
        } finally {
            setLoading(false);
        }
    }, [currentEventId]);

    useEffect(() => {
        if (currentEventId) fetchEventData();
    }, [currentEventId, fetchEventData]);

    const saveVenuesData = async (newVenues) => {
        if (planningEvent) { message.error('Edit competition groups and times on the Admin Page.'); return; }
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            await updateDoc(docRef, { venues: newVenues });
            setVenues(newVenues);
            message.success("Sessions updated successfully");
        } catch (error) {
            console.error("Error saving venues:", error);
            message.error("Failed to save sessions");
        }
    };

    // --- Dates ---
    const handleAddDate = (venueId) => {
        setSelectedVenueId(venueId);
        dateForm.resetFields();
        setIsDateModalVisible(true);
    };

    const onDateSubmit = async () => {
        try {
            const values = await dateForm.validateFields();
            const dateStr = values.date.format('YYYY-MM-DD');
            
            const newVenues = venues.map(v => {
                if (v.id === selectedVenueId) {
                    const venueSessions = v.sessions || {};
                    if (venueSessions[dateStr]) {
                        message.error("This date already exists for this venue!");
                        return v;
                    }
                    return {
                        ...v,
                        sessions: {
                            ...venueSessions,
                            [dateStr]: []
                        }
                    };
                }
                return v;
            });

            // Check if we actually made a change (didn't return early due to error)
            const targetVenue = newVenues.find(v => v.id === selectedVenueId);
            if (targetVenue.sessions[dateStr]) {
                await saveVenuesData(newVenues);
                setIsDateModalVisible(false);
            }
        } catch (err) {
            console.log("Validation failed", err);
        }
    };

    const deleteDate = async (venueId, dateStr) => {
        const newVenues = venues.map(v => {
            if (v.id === venueId) {
                const newSessions = { ...(v.sessions || {}) };
                delete newSessions[dateStr];
                return { ...v, sessions: newSessions };
            }
            return v;
        });
        await saveVenuesData(newVenues);
    };

    // --- Timeslots ---
    const handleAddTime = (venueId, dateStr) => {
        setSelectedVenueId(venueId);
        setSelectedDate(dateStr);
        timeForm.resetFields();
        setIsTimeModalVisible(true);
    };

    const onTimeSubmit = async () => {
        try {
            const values = await timeForm.validateFields();
            const [start, end] = values.timeRange;
            const timeStr = `${start.format('HH:mm')}-${end.format('HH:mm')}`;
            
            const newVenues = venues.map(v => {
                if (v.id === selectedVenueId) {
                    const venueSessions = v.sessions || {};
                    const timeslots = [...(venueSessions[selectedDate] || [])];

                    if (timeslots.includes(timeStr)) {
                        message.error("This timeslot already exists!");
                        return v;
                    }

                    timeslots.push(timeStr);
                    timeslots.sort();

                    return {
                        ...v,
                        sessions: {
                            ...venueSessions,
                            [selectedDate]: timeslots
                        }
                    };
                }
                return v;
            });

            const targetVenue = newVenues.find(v => v.id === selectedVenueId);
            if (targetVenue.sessions[selectedDate].includes(timeStr)) {
                await saveVenuesData(newVenues);
                setIsTimeModalVisible(false);
            }
        } catch (err) {
            console.log("Validation failed", err);
        }
    };

    const deleteTime = async (venueId, dateStr, timeStr) => {
        const newVenues = venues.map(v => {
            if (v.id === venueId) {
                const venueSessions = v.sessions || {};
                const timeslots = (venueSessions[dateStr] || []).filter(t => t !== timeStr);
                return {
                    ...v,
                    sessions: {
                        ...venueSessions,
                        [dateStr]: timeslots
                    }
                };
            }
            return v;
        });
        await saveVenuesData(newVenues);
    };

    const handleGenerateSeats = async (venueId, dateStr) => {
        if (planningEvent) { message.error('Generate and verify seats on the Admin Page.'); return; }
        const targetVenue = venues.find(v => v.id === venueId);
        const timeslots = targetVenue?.sessions?.[dateStr] || [];
        if (timeslots.length === 0) {
            message.warning("No timeslots found for this date. Please add timeslots first.");
            return;
        }

        const sessionsArray = timeslots.map(timeStr => `${dateStr}_${timeStr}`);
        
        try {
            setLoading(true);
            await uploadFullSeatLayout(currentEventId || 'APCS2026', venueId, sessionsArray);
            message.success(`Seats generated successfully for ${sessionsArray.length} sessions.`);
            // Refresh to get updated generation status
            await fetchEventData();
        } catch (error) {
            console.error("Error generating seats:", error);
            message.error("Failed to generate seats. See console for details.");
        } finally {
            setLoading(false);
        }
    };

    const openDraftSlot = (slot = null) => {
        setEditingSlot(slot);
        slotForm.resetFields();
        slotForm.setFieldsValue({
            venueId: slot?.venueId,
            date: slot?.date ? dayjs(slot.date) : undefined,
            timeRange: slot?.start && slot?.end
                ? [dayjs(`2000-01-01T${slot.start}`), dayjs(`2000-01-01T${slot.end}`)] : undefined,
        });
        setSlotModalOpen(true);
    };

    const saveDraftSlot = async values => {
        setSlotSaving(true);
        try {
            const slot = {
                slotId: editingSlot?.slotId,
                venueId: values.venueId,
                date: values.date.format('YYYY-MM-DD'),
                start: values.timeRange?.[0]?.format('HH:mm') || null,
                end: values.timeRange?.[1]?.format('HH:mm') || null,
            };
            const response = await apis.competitionPlanning.saveSlot(currentEventId, planningState.revision, slot);
            if (response?.error) throw new Error(response.error.data?.message || 'Could not save draft session.');
            setSlotModalOpen(false);
            await fetchEventData();
            message.success('Draft session saved.');
        } catch (error) {
            message.error(error.message);
        } finally {
            setSlotSaving(false);
        }
    };

    const removeDraftSlot = async slotId => {
        try {
            const response = await apis.competitionPlanning.deleteSlot(currentEventId, slotId, planningState.revision);
            if (response?.error) throw new Error(response.error.data?.message || 'Could not delete draft session.');
            await fetchEventData();
            message.success('Draft session deleted.');
        } catch (error) {
            message.error(error.message);
        }
    };

    if (planningEvent && planningState?.status === 'draft') {
        return <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', borderRadius: 8 }}>
            <Title level={3}>Performer Session Setup</Title>
            <Alert showIcon type="info" style={{ marginBottom: 16 }}
                message="Draft competition sessions"
                description="Create a venue and date here, add the final time when known, then select the session for a group on the Admin Page. Ticket sales open only after publication and seat verification." />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openDraftSlot()} style={{ marginBottom: 16 }}>Add draft session</Button>
            {(planningState.draftSlots || []).length === 0 && <Text type="secondary">No draft sessions yet.</Text>}
            {(planningState.draftSlots || []).map(slot => {
                const linkedGroup = (planningState.groups || []).find(group => group.slotId === slot.slotId);
                const venue = venues.find(item => item.id === slot.venueId);
                return <div key={slot.slotId} style={{ padding: 12, border: '1px solid #d9d9d9', borderRadius: 8, marginBottom: 8 }}>
                    <Space wrap>
                        <Text strong>{venue?.label || slot.venueId}</Text>
                        <Text>{slot.date}</Text>
                        <Tag color={slot.start ? 'blue' : 'orange'}>{slot.start ? `${slot.start}-${slot.end}` : 'Time pending'}</Tag>
                        {linkedGroup && <Tag color="green">{linkedGroup.label}</Tag>}
                        <Button size="small" onClick={() => openDraftSlot(slot)}>Edit</Button>
                        <Popconfirm title="Delete draft session?" onConfirm={() => removeDraftSlot(slot.slotId)} disabled={Boolean(linkedGroup)}>
                            <Button size="small" danger disabled={Boolean(linkedGroup)}>Delete</Button>
                        </Popconfirm>
                    </Space>
                </div>;
            })}
            <Modal title={editingSlot ? 'Edit draft session' : 'Add draft session'} open={slotModalOpen}
                onCancel={() => setSlotModalOpen(false)} onOk={() => slotForm.submit()} confirmLoading={slotSaving}>
                <Form form={slotForm} layout="vertical" onFinish={saveDraftSlot}>
                    <Form.Item name="venueId" label="Venue" rules={[{ required: true }]}>
                        <Select options={venues.map(venue => ({ value: venue.id, label: venue.label || venue.id }))} />
                    </Form.Item>
                    <Form.Item name="date" label="Performance date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="timeRange" label="Final time (optional until publication)">
                        <TimePicker.RangePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </Content>;
    }

    return (
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', borderRadius: 8 }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={3} style={{ margin: 0 }}>Performer Session Setup</Title>
                <Text type="secondary">
                    Manage the schedule of dates and timeslots for performer session assignments. 
                    Venues are managed in the <strong>Venue Settings</strong> page.
                </Text>
            </div>

            {planningError && <Alert showIcon type="error" style={{ marginBottom: 16 }} message="Could not load session planning" description={planningError} action={<Button onClick={fetchEventData}>Retry</Button>} />}
            {planningEvent && <Alert showIcon type="info" style={{ marginBottom: 16 }}
                message="Published competition schedule"
                description="Published sessions are read-only here. Use the Admin Page to generate and verify numbered seats before ticket sales." />}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>Loading...</div>
            ) : venues.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>
                    No venues found. Please create venues in "Venue Settings" first.
                </div>
            ) : (
                <Collapse defaultActiveKey={venues.map(v => v.id)}>
                    {venues.map((venue) => (
                        <Panel
                            key={venue.id}
                            header={
                                <Space>
                                    <Text strong style={{ fontSize: '16px' }}>{venue.label}</Text>
                                    <Text type="secondary">({venue.id})</Text>
                                </Space>
                            }
                        >
                            {!planningEvent && <div style={{ marginBottom: 16 }}>
                                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => handleAddDate(venue.id)}>
                                    Add Date
                                </Button>
                            </div>}

                            {Object.keys(venue.sessions || {}).sort().map(dateStr => (
                                <div key={dateStr} style={{ marginBottom: 24, padding: '12px 16px', background: '#f5f5f5', borderRadius: 8 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <Text strong>{dayjs(dateStr).format('dddd, DD MMM YYYY')}</Text>
                                        {!planningEvent && <Space>
                                            <Button size="small" type={(venue.sessions[dateStr] || []).length > 0 && (venue.sessions[dateStr] || []).every(t => sessionsSeatsGenerated[`${venue.id}_${dateStr}_${t}`]) ? "default" : "primary"} onClick={() => handleGenerateSeats(venue.id, dateStr)}>
                                                {(venue.sessions[dateStr] || []).length > 0 && (venue.sessions[dateStr] || []).every(t => sessionsSeatsGenerated[`${venue.id}_${dateStr}_${t}`]) ? "Regenerate Seats" : "Generate Seats"}
                                            </Button>
                                            <Button size="small" type="primary" ghost icon={<PlusOutlined />} onClick={() => handleAddTime(venue.id, dateStr)}>
                                                Add Timeslot
                                            </Button>
                                            <Popconfirm
                                                title="Delete Date"
                                                description="Are you sure you want to delete this date and ALL its timeslots?"
                                                onConfirm={() => deleteDate(venue.id, dateStr)}
                                                okText="Yes"
                                                cancelText="No"
                                            >
                                                <Button size="small" danger icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                        </Space>}
                                    </div>

                                    <div>
                                        {!venue.sessions[dateStr] || venue.sessions[dateStr].length === 0 ? (
                                            <Text type="secondary" italic>No timeslots defined</Text>
                                        ) : (
                                            <Space size={[8, 8]} wrap>
                                                {venue.sessions[dateStr].map(timeStr => (
                                                    <Tag
                                                        key={timeStr}
                                                        closable={!planningEvent}
                                                        onClose={(e) => {
                                                            e.preventDefault();
                                                            deleteTime(venue.id, dateStr, timeStr);
                                                        }}
                                                        icon={<ClockCircleOutlined />}
                                                        style={{ padding: '4px 8px', fontSize: '14px' }}
                                                        color={sessionsSeatsGenerated[`${venue.id}_${dateStr}_${timeStr}`] ? "green" : "blue"}
                                                    >
                                                        {timeStr} {sessionsSeatsGenerated[`${venue.id}_${dateStr}_${timeStr}`] && "✓ Generated"}
                                                    </Tag>
                                                ))}
                                            </Space>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </Panel>
                    ))}
                </Collapse>
            )}

            {/* Date Modal */}
            <Modal
                title={`Add Date for ${venues.find(v => v.id === selectedVenueId)?.label || selectedVenueId}`}
                open={isDateModalVisible}
                onOk={onDateSubmit}
                onCancel={() => setIsDateModalVisible(false)}
            >
                <Form form={dateForm} layout="vertical">
                    <Form.Item
                        name="date"
                        label="Performance Date"
                        rules={[{ required: true, message: 'Please select a date' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Time Modal */}
            <Modal
                title={`Add Timeslot for ${venues.find(v => v.id === selectedVenueId)?.label || selectedVenueId} on ${selectedDate}`}
                open={isTimeModalVisible}
                onOk={onTimeSubmit}
                onCancel={() => setIsTimeModalVisible(false)}
            >
                <Form form={timeForm} layout="vertical">
                    <Form.Item
                        name="timeRange"
                        label="Time Range (Start - End)"
                        rules={[{ required: true, message: 'Please select a time range' }]}
                    >
                        <TimePicker.RangePicker 
                            format="HH:mm" 
                            style={{ width: '100%' }} 
                            minuteStep={5}
                            placeholder={['Start Time', 'End Time']}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Content>
    );
};

export default PerformerSessionsSettings;
